import type {ApplicationInsights} from '@microsoft/applicationinsights-web'
import {DataType, type QueueItem, RequestType} from '@/models/queue'

export const TELEMETRY_EVENT = {
  appStartedOffline: 'AppStartedOffline',
  offlinePeriod: 'OfflinePeriod',
  queueItemAdded: 'QueueItemAdded',
  queueProcessed: 'QueueProcessed',
} as const

export type TelemetryProperties = Record<string, string | number | boolean | undefined>

interface BacklogEntry {
  name: string
  properties?: TelemetryProperties
  occurredAt: string
}

const BACKLOG_STORAGE_KEY = 'sbl.telemetryBacklog'
const MAX_BACKLOG_LENGTH = 200

let client: ApplicationInsights | undefined

export const setTelemetryClient = (appInsights: ApplicationInsights | undefined): void => {
  client = appInsights
}

const readBacklog = (): BacklogEntry[] => {
  try {
    const raw = localStorage.getItem(BACKLOG_STORAGE_KEY)
    return raw ? JSON.parse(raw) as BacklogEntry[] : []
  } catch {
    return []
  }
}

const writeBacklog = (entries: BacklogEntry[]): void => {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(BACKLOG_STORAGE_KEY)
    } else {
      localStorage.setItem(BACKLOG_STORAGE_KEY, JSON.stringify(entries.slice(-MAX_BACKLOG_LENGTH)))
    }
  } catch {
  }
}

const send = (name: string, properties?: TelemetryProperties): void => {
  client?.trackEvent({name}, properties)
}

export const trackEvent = (name: string, properties?: TelemetryProperties): void => {
  try {
    if (!client) {
      return
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      writeBacklog([...readBacklog(), {name, properties, occurredAt: new Date().toISOString()}])
      return
    }
    send(name, properties)
  } catch {
  }
}

export const flushTelemetryBacklog = (): void => {
  try {
    if (!client || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      return
    }
    const backlog = readBacklog()
    if (backlog.length === 0) {
      return
    }
    writeBacklog([])
    for (const entry of backlog) {
      send(entry.name, {...entry.properties, occurredAt: entry.occurredAt, sentFromBacklog: true})
    }
  } catch {
  }
}

export const trackQueueItemAdded = (item: QueueItem, queueSize: number | undefined): void => {
  trackEvent(TELEMETRY_EVENT.queueItemAdded, {
    dataType: DataType[item.type] ?? String(item.type),
    requestType: typeof item.requestType === 'string' ? item.requestType : RequestType[item.requestType],
    queueSize,
  })
}
